/**
 * Mutually exclusive checkbox sets, mirroring how a PDF's own radio group behaves: the boxes of one
 * set share an `exclusiveGroup`, and ticking any of them clears the rest. The server re-applies the
 * same rule on every save, so what happens here is only about the form reading correctly while it
 * is being filled in.
 */

export const CHECKED_VALUE = "true";
export const UNCHECKED_VALUE = "";

export const isChecked = (values, name) => values?.[name] === CHECKED_VALUE;

/** The set a checkbox belongs to, or null when it stands on its own. */
export const exclusiveGroupOf = (field) => {
  if (!field || field.fieldType !== "CHECKBOX") return null;
  const group = typeof field.exclusiveGroup === "string" ? field.exclusiveGroup.trim() : "";
  return group || null;
};

/** Names of every other checkbox sharing this field's set. Empty when it is ungrouped. */
export const exclusiveSiblingNames = (fields, field) => {
  const group = exclusiveGroupOf(field);
  if (!group) return [];
  return (fields || [])
    .filter((other) => other.name !== field.name && exclusiveGroupOf(other) === group)
    .map((other) => other.name);
};

/**
 * New field values with this checkbox set to `checked`. Ticking one clears its siblings; unticking
 * clears only itself, so a client who picked the wrong option can back all the way out of the set
 * rather than being stuck with one answer forced on them.
 */
export const applyCheckboxChange = (fields, values, field, checked) => {
  if (!checked) {
    return { ...values, [field.name]: UNCHECKED_VALUE };
  }
  const clearedSiblings = Object.fromEntries(
    exclusiveSiblingNames(fields, field).map((name) => [name, UNCHECKED_VALUE])
  );
  return { ...values, ...clearedSiblings, [field.name]: CHECKED_VALUE };
};
